import { useFormik } from 'formik';
import styles from '../../Consent Request/Create/CreateConsentRequest.module.css';
import { consentTemplateSchema } from '../../../schemas';
import {
	generateSahamatiTemplateRules,
	generateApprovedConsentTemplates,
	modifyApprovedConsentTemplate,
	submitTemplateCreationRequest,
	fetchTemplateData
} from '../../../utils/common.utils';
import Heading from '../../../components/Heading/Heading';
import infoIcon from '../../../assets/img/info.svg';
import { Tooltip } from 'primereact/tooltip';
import Icon from '../../../components/Icon/Icon';
import { InputText } from 'primereact/inputtext';
import Error from '../../../components/Error/Error';
import { Dropdown } from 'primereact/dropdown';
import { useState, useEffect, Fragment } from 'react';
import CONSTANTS from '../../../constants';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import dataRangeList from '../../../constants/dataRangeList';
import { toast } from 'react-toastify';
import CustomPurposeTextInput from '../TemplateFormFields/CustomPurposeTextInput';

const CreateTemplates = (props) => {
	const { isEditable, templateDetails } = props;
	const [templateData, setTemplateData] = useState();
	const [isLoading, setIsLoading] = useState(false);
	const [loadingError, setLoadingError] = useState(null);
	const [fieldsPrefilledFromAPI, setFieldsPrefilledFromAPI] = useState(false);
	const [filteredCustomPurposeText, setFilteredCustomPurposeText] = useState([]);

	const [originalValues, setOriginalValues] = useState({});

	const [sahamatiCodeList, setSahamatiCodeList] = useState([]);
	useEffect(() => {
		generateSahamatiTemplateRules(setSahamatiCodeList);
	}, []);

	const mapDataRangeUnit = (startMonths) => {
		const mappedUnit = startMonths === 0 ? 'current' : `last ${startMonths}`;
		return dataRangeList.find((item) => item === mappedUnit) || '';
	};

	const {
		templateName = '',
		Purpose: purpose = [],
		templateDescription = '',
		consentMode = '',
		consentTypes = [],
		fiTypes: fiType = [],
		fetchType: frequency = '',
		Frequency: frequencyPeriod = {},
		ConsentExpiry: consentExpiry = {},
		dataRangeEndMonths: financialDataRangeEndMonths = '',
		dataRangeStartMonths: financialDataRangeStartMonths = '',
		DataLife: dataLife,
		sahamatiCode = '',
		businessPurpose = '',
	} = templateDetails || {};

	const dataRangeUnit = mapDataRangeUnit(financialDataRangeStartMonths || 0);
	const dataRangeValue = financialDataRangeEndMonths || 0;

	const {
		values,
		touched,
		errors,
		handleBlur,
		handleChange,
		handleSubmit,
		setFieldValue,
		resetForm,
	} = useFormik({
		initialValues: CONSTANTS.initialConsentTemplateValues,
		validationSchema: consentTemplateSchema,
		onSubmit: (values, actions) => {
			isEditable
				? modifyApprovedConsentTemplate(values)
				: submitTemplateCreationRequest(values);
			actions.resetForm();
		},
		consentExpiry: {
			value: '',
			unit: '',
			isValidUnit: false 
		}
	});

	useEffect(() => {
		const mapDataRangeUnit = (startMonths) => {
			const mappedUnit = startMonths === 0 ? 'current' : `last ${startMonths}`;
			return dataRangeList.find((item) => item === mappedUnit) || '';
		};
	
		if (values.sahamatiCode) {
			fetchTemplateData(
				values.sahamatiCode, 
				setFieldValue, 
				templateData, 
				setIsLoading, 
				setLoadingError, 
				setFieldsPrefilledFromAPI, 
				setOriginalValues,
				mapDataRangeUnit
			);
		} else {
			setFieldsPrefilledFromAPI(false);
		}
	}, [values.sahamatiCode, setFieldValue, templateData]);

	//Can remove this API call if purpose from ReBit remain static
	useEffect(() => {
		generateApprovedConsentTemplates(setTemplateData);

		if (isEditable && templateDetails) {
			setFieldValue('templateName', templateName);
			setFieldValue('templateDescription', templateDescription);
			setFieldValue('purpose', purpose);
			setFieldValue('consentMode', consentMode);
			setFieldValue('fiType', fiType);
			setFieldValue('frequency', frequency);
			setFieldValue('frequencyPeriod', frequencyPeriod);
			setFieldValue('consentType', consentTypes);
			setFieldValue('consentExpiry', consentExpiry);
			setFieldValue('dataRange.value', dataRangeValue);
			setFieldValue('dataRange.unit', dataRangeUnit);
			setFieldValue('dataLife', dataLife);
			setFieldValue('sahamatiCode', sahamatiCode);
			setFieldValue('businessPurpose', businessPurpose);
			setFieldValue('customPurposeText', purpose?.customPurposeText || '');
		}
		// The prefilled values all come from templateDetails, which is listed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateDetails, setFieldValue, isEditable]);

	//List of purposes
	const purposes = templateData?.purpose;
	//List of purpose texts of consent request
	const purposeCode = templateData?.purpose?.map((purpose) => ({
		label: `${purpose?.code || ''}`,
		value: purpose.text,
	}));

	//List of FI Types
	const fiTypes = templateData?.fiTypes?.map(
		(fiType) => fiType.fiTypeDescription
	);

	//List of Consent Types
	const consentType = templateData?.consentTypes;
	const handlePurposeChange = (event) => {
		const selectedPurpose = purposes.find(
			(purpose) => purpose.text === event.target.value
		);
		setFieldValue('purpose', selectedPurpose);
		setFieldValue('customPurposeText', selectedPurpose?.text || '');
	};

	const handleConsentTypeChange = (e, option) => {
		const isChecked = e.checked;
		const optionIndex = values.consentType.indexOf(option);
		let newOptions = [...values.consentType];

		if (isChecked && optionIndex === -1) {
			newOptions.push(option);
		} else if (!isChecked && optionIndex !== -1) {
			newOptions.splice(optionIndex, 1);
		}
		setFieldValue('consentType', newOptions);
	};

	const handleConsentExpiryUnitChange = (event) => {
		const unit = event.target.value;
		setFieldValue('consentExpiry.unit', unit);
		// Also update data range end unit to match
		setFieldValue('dataRangeEndUnit', unit);
	};

	const handleConsentExpiryValueChange = (event) => {
		const value = event.target.value;

		const processedValue = value === '0' ? '' : value;
		// Validate against original value
		if (validateFieldValue('consentExpiry', 'consentExpiry.value', processedValue)) {
			setFieldValue('consentExpiry.value', processedValue);

			// updates the data range value to match
			setFieldValue('dataRange.value', processedValue);
		}
	};

	const handleSahamatiCodeChange = (e) => {
		setFieldValue('sahamatiCode', e.value);
		//Reset the flag when Sahamati code changes
		setFieldsPrefilledFromAPI(false);
	}

	const validateFieldValue = (fieldName, fieldPath, newValue) => {
		if (!fieldsPrefilledFromAPI || originalValues[`${fieldName}NA`] === true) {
			return true;
		}
	
		if (!originalValues[fieldName]) return true;
		
		const originalValue = originalValues[fieldName];

		if (newValue === '' || newValue === null || newValue === undefined) {
			return true;
		}

		if (parseInt(newValue) > parseInt(originalValue)) {
			toast.error(`Please use a value equal to or lower than the recommended value of ${originalValue}.`, {
				position: "top-right",
				autoClose: 4000,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true
			});
			setFieldValue(fieldPath, originalValue === 0 ? '' : originalValue);
			return false;
		}
		return true;
	};

const handleDataLifeValueChange = (event) => {
    const value = event.target.value;
    if (validateFieldValue('dataLife', 'dataLife.value', value)) {
        setFieldValue('dataLife.value', value);
    }
};

const handleDataRangeValueChange = (event) => {
    const value = event.target.value;
    if (validateFieldValue('dataRange', 'dataRange.value', value)) {
        setFieldValue('dataRange.value', value);
    }
};

const handleFrequencyPeriodValueChange = (event) => {
    const value = event.target.value;
    if (validateFieldValue('frequencyPeriod', 'frequencyPeriod.value', value)) {
        setFieldValue('frequencyPeriod.value', value);
    }
};

const validateDataRangeUnit = (selectedValue) => {
	if (!fieldsPrefilledFromAPI || !originalValues.dataRangeStartValue) {
	  return true;
	}
	
	// Convert values to numeric for comparison
	const getMonthsValue = (value) => {
	  if (value === 'current') return 0;
	  const match = value.match(/last (\d+)/);
	  return match ? parseInt(match[1], 10) : 0;
	};
	
	const selectedMonths = getMonthsValue(selectedValue);
	const originalMonths = getMonthsValue(originalValues.dataRangeStartValue);
	
	if (selectedMonths > originalMonths) {
	  toast.error(`Please select a data range equal to or lower than the recommended value of ${originalValues.dataRangeStartValue}.`, {
		position: "top-right",
		autoClose: 4000,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true
	  });
	  return false;
	}
	return true;
  };

const handleDataRangeUnitChange = (event) => {
	const unit = event.value;
	if (validateDataRangeUnit(unit)) {
	  setFieldValue('dataRange.unit', unit);
	} else {
	  // If validation fails, revert to the original API-filled value
	  if (fieldsPrefilledFromAPI && originalValues.dataRangeStartValue) {
		setFieldValue('dataRange.unit', originalValues.dataRangeStartValue);
	  }
	}
  };
  
//To keep dataRange.value in sync with consentExpiry.value
useEffect(() => {
    if (values.consentExpiry.value !== undefined) {
        setFieldValue('dataRange.value', values.consentExpiry.value);
    }
}, [values.consentExpiry.value, setFieldValue]);

//To keep units in sync
useEffect(() => {
	if (values.consentExpiry.unit !== undefined) {
	  setFieldValue('dataRangeEndUnit', values.consentExpiry.unit);
	}
  }, [values.consentExpiry.unit, setFieldValue]);

	return (
		<div className={isEditable ? '' : 'main'}>
			{isEditable ? '' : <Heading>Create Consent Template</Heading>}
			<form
				className={`${styles['consent-request__container']} ${styles.card}`}
				onSubmit={handleSubmit}
			>
				<div className={styles.form}>
					{/* Template Name */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="template" className={styles['input-label']}>
								Enter template name:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} templateName-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Enter a name for your consent template."
							/>
						</div>
						<Tooltip
							target=".templateName-tooltip"
							className={styles.tooltip}
						/>
						<div className="p-inputgroup">
							<Icon icon="content_paste" className="p-inputgroup-addon" />
							<InputText
								className={`${touched.templateName && errors.templateName ? 'p-invalid' : ''
									} styles.input`}
								id="templateName"
								name="templateName"
								placeholder="Enter template name"
								disabled={isEditable}
								value={values.templateName || ''}
								onChange={handleChange}
								onBlur={handleBlur}
							/>
						</div>
						{touched.templateName && errors.templateName ? (
							<Error>{errors.templateName}</Error>
						) : null}
					</div>
					{/* Consent Description */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="templateDescription" className={styles['input-label']}>
								Consent Description:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} templateDesc-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Give a small description indicating what the consent template is about."
							/>
						</div>
						<Tooltip
							target=".templateDesc-tooltip"
							className={styles.tooltip}
						/>
						<div className="p-inputgroup">
							<Icon icon="description" className="p-inputgroup-addon" />
							<InputText
								id="templateDescription"
								name="templateDescription"
								className={`${touched.templateDescription && errors.templateDescription ? 'p-invalid' : ''
									} styles.input`}
								placeholder="Enter Consent Description"
								value={values.templateDescription || ''}
								onChange={handleChange}
								onBlur={handleBlur}
								maxLength={128}
							/>
						</div>
						{touched.templateDescription && errors.templateDescription ? (
							<Error>{errors.templateDescription}</Error>
						) : null}
					</div>
					
					{/* Sahamati Code - with Dropdown */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="sahamatiCode" className={styles['input-label']}>
								Sahamati Fair use Template ID:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} sahamatiCode-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Select the Sahamati code to auto-fill template values."
							/>
						</div>
						<Tooltip target=".sahamatiCode-tooltip" className={styles.tooltip} />
						<div className="p-inputgroup">
							<Icon icon="code" className="p-inputgroup-addon" />
							<Dropdown
							name="sahamatiCode"
							id="sahamatiCode"
							value={values.sahamatiCode || ''}
							onChange={handleSahamatiCodeChange}
							onBlur={handleBlur}
							placeholder="Select a Sahamati Template ID"
							options={sahamatiCodeList}
							className={`${touched.sahamatiCode && errors.sahamatiCode ? 'p-invalid' : ''}`}
							/>
						</div>
						{isLoading && <small className={styles.helper}>Loading template data...</small>}
						{loadingError && <Error>{loadingError}</Error>}
						{touched.sahamatiCode && errors.sahamatiCode ? (
						<Error>{errors.sahamatiCode}</Error>
						) : null}
					</div>


					{/* Business Purpose - NEW FIELD */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="businessPurpose" className={styles['input-label']}>
								Business Purpose:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} businessPurpose-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Enter the business purpose for this consent template."
							/>
						</div>
						<Tooltip target=".businessPurpose-tooltip" className={styles.tooltip} />
						<div className="p-inputgroup">
							<Icon icon="business" className="p-inputgroup-addon" />
							<InputText
								id="businessPurpose"
								name="businessPurpose"
								className={`${touched.businessPurpose && errors.businessPurpose ? 'p-invalid' : ''
									} styles.input`}
								placeholder="Enter Business Purpose"
								value={values.businessPurpose || ''}
								onChange={handleChange}
								onBlur={handleBlur}
								maxLength={256}
							/>
						</div>
						{touched.businessPurpose && errors.businessPurpose ? (
							<Error>{errors.businessPurpose}</Error>
						) : null}
					</div>

					{/* Purpose of Consent Code */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="purpose" className={styles['input-label']}>
								Purpose Code:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} purpose-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Enter the purpose of the consent template."
							/>
						</div>
						<Tooltip target=".purpose-tooltip" className={styles.tooltip} />
						<div className="p-inputgroup">
							<Icon icon="question_exchange" className="p-inputgroup-addon" />
							<Dropdown
								name="purpose.text"
								id="purpose.text"
								value={values.purpose?.text || ''}
								onChange={handlePurposeChange}
								onBlur={handleBlur}
								placeholder="Enter your purpose for consent request"
								options={purposeCode}
								disabled={fieldsPrefilledFromAPI && values.purpose?.text}
							/>
						</div>
						<small className={styles.helper}>
							For more information on purposes, click{' '}
							<a
								href="https://sahamati.org.in/aa-fair-use-template-library/"
								target="_blank"
								rel="noreferrer noopener"
							>
								here.
							</a>
						</small>
						{touched.purpose && errors.purpose ? (
							<Error>{errors.purpose.code}</Error>
						) : null}
					</div>
					
					{/* Purpose Text Input */}
					<CustomPurposeTextInput
						value={values.customPurposeText}
						purpose={purpose}
						filteredCustomPurposeText={filteredCustomPurposeText}
						setFilteredCustomPurposeText={setFilteredCustomPurposeText}
						setFieldValue={setFieldValue}
						touched={touched}
						errors={errors}
					/>

					{/* Consent Mode */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="consentMode" className={styles['input-label']}>
								Consent Mode:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} consentMode-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Select one of the modes of operation for the consent template."
							/>
						</div>
						<Tooltip target=".consentMode-tooltip" className={styles.tooltip} />
						<div className="p-inputgroup">
							<Icon icon="tune" className="p-inputgroup-addon" />
							<Dropdown
								placeholder="Select a mode"
								name="consentMode"
								inputId="consentMode"
								value={values.consentMode || ''}
								onChange={handleChange}
								onBlur={handleBlur}
								options={CONSTANTS.consentModeList}
								disabled
							/>
						</div>
						{touched.consentMode && errors.consentMode ? (
							<Error>{errors.consentMode}</Error>
						) : null}
					</div>
					{/* FI Type */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="fiType" className={styles['input-label']}>
								FI Type:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} fiType-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Select the type of financial information to be fetched using the template."
							/>
						</div>
						<Tooltip target=".fiType-tooltip" className={styles.tooltip} />
						<div className="p-inputgroup">
							<Icon icon="account_balance" className="p-inputgroup-addon" />
							<MultiSelect
								name="fiType"
								id="fiType"
								options={fiTypes}
								value={values.fiType}
								onChange={(e) => setFieldValue('fiType', e.value)}
								onBlur={handleBlur}
								placeholder="Select FI Type(s)"
							/>
						</div>
						{touched.fiType && errors.fiType ? (
							<Error>{errors.fiType}</Error>
						) : null}
					</div>
					{/* Consent Type  */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="consentType" className={styles['input-label']}>
								Consent Type:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} consentType-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Select the type of account data you want to fetch using the template."
							/>
						</div>
						<Tooltip target=".consentType-tooltip" className={styles.tooltip} />
						<div className={styles.options}>
							<div className={styles['options__container']}>
								{consentType?.map((option) => (
									<Fragment key={option}>
										<Checkbox
											id={option}
											name="consentType"
											value={option || []}
											onChange={(e) => handleConsentTypeChange(e, option)}
											onBlur={handleBlur}
											checked={values.consentType?.includes(option)}
										/>
										<label htmlFor={option}>{option}</label>
									</Fragment>
								))}
							</div>
						</div>
						{touched.consentType && errors.consentType ? (
							<Error>{errors.consentType}</Error>
						) : null}
					</div>
					<div className={styles.divider}>
						<h3>Consent Usage Frequency</h3>
						<Divider />
					</div>
					{/* Frequency */}
					<div className={styles['input__container-full']}>
						<div className={styles['label__container']}>
							<label htmlFor="frequency" className={styles['input-label']}>
								Frequency:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} frequency-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Select the frequency for fetching the data using the template."
							/>
						</div>
						<Tooltip target=".frequency-tooltip" className={styles.tooltip} />
						<div className="p-inputgroup">
							<div className={styles['options__container']}>
								<RadioButton
									name="frequency"
									inputId="frequency1"
									value="ONETIME"
									onChange={handleChange}
									onBlur={handleBlur}
									checked={values.frequency === 'ONETIME'}
									disabled={fieldsPrefilledFromAPI && values.frequency}
								/>
								<label htmlFor="frequency1" className={styles['options-label']}>
									One Time
								</label>
							</div>
							<div className={styles['options__container']}>
								<RadioButton
									name="frequency"
									inputId="frequency2"
									value="PERIODIC"
									onChange={handleChange}
									onBlur={handleBlur}
									checked={values.frequency === 'PERIODIC'}
									disabled={fieldsPrefilledFromAPI && values.frequency}
								/>
								<label htmlFor="frequency2" className={styles['options-label']}>
									Periodic
								</label>
							</div>
						</div>
						{touched.frequency && errors.frequency ? (
							<Error>{errors.frequency}</Error>
						) : null}
						{values.frequency === 'PERIODIC' ? (
							<div className={styles.periodic}>
								Data Request can be done
								<span>
									<InputText
										name="frequencyPeriod.value"
										id="frequencyPeriod.value"
										value={values.frequencyPeriod.value}
										onChange={handleFrequencyPeriodValueChange}
										onBlur={handleBlur}
										className={styles['periodic-input']}
									/>
								</span>
								time(s)
								<Dropdown
									name="frequencyPeriod.unit"
									id="frquencyPeriod.unit"
									options={CONSTANTS.frequencyUnitList}
									value={values.frequencyPeriod.unit}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Select fetch time unit"
									className={styles['periodic-dropdown']}
									disabled={fieldsPrefilledFromAPI && values.frequencyPeriod.unit}
								/>
								(s)
							</div>
						) : null}
						{values.frequency === 'PERIODIC' ? (
							<div className={styles['multi-error']}>
								{touched.frequencyPeriod && errors.frequencyPeriod ? (
									<Error>{errors.frequencyPeriod.value}</Error>
								) : null}
								{touched.frequencyPeriod && errors.frequencyPeriod ? (
									<Error>{errors.frequencyPeriod.unit}</Error>
								) : null}
							</div>
						) : null}
					</div>
					<div className={styles.divider}>
						<h3>Consent Time Period Parameters</h3>
						<Divider />
					</div>
					{/* Consent Start Date */}
					{/* <div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="consentStart" className={styles['input-label']}>
								Consent Start Date:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} consentStart-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Enter the start date for fetching the data using the template."
							/>
						</div>
						<Tooltip
							target=".consentStart-tooltip"
							className={styles.tooltip}
						/>
						<div>
							Consent will start after
							<span>
								<InputText
									keyfilter="int"
									name="consentStart"
									id="consentStart"
									value={values.consentStart}
									onChange={handleChange}
									onBlur={handleBlur}
									className={styles['periodic-input']}
								/>
							</span>
							day(s)
						</div>
						{touched.consentStart && errors.consentStart ? (
							<Error>{errors.consentStart}</Error>
						) : null}
						<small className={styles.helper}>
							Note: Value of '0' will be taken as current date for the consent
							request.
						</small>
					</div> */}
					{/* Consent Expiry Date */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="consentExpiry" className={styles['input-label']}>
								Consent Expiry Date:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} consentExpiry-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Enter the date after which consent will expire using this template."
							/>
						</div>
						<Tooltip
							target=".consentExpiry-tooltip"
							className={styles.tooltip}
						/>
						<div className={styles['options__container']}>
							Consent will expire after
							<span>
								<InputText
									keyfilter="int"
									name="consentExpiry.value"
									value={values.consentExpiry.value === 0 ? '' : values.consentExpiry.value}
									onChange={handleConsentExpiryValueChange}
									onBlur={handleBlur}
									className={styles['periodic-input']}
								/>
							</span>
							<Dropdown
								inputId="expiry-unit"
								name="consentExpiry.unit"
								options={CONSTANTS.dateUnitList}
								value={values.consentExpiry.unit}
								onChange={handleConsentExpiryUnitChange}
								onBlur={handleBlur}
								placeholder="Select an expiry date unit"
								className={styles['periodic-dropdown']}
								disabled={fieldsPrefilledFromAPI && values.consentExpiry.unit}
							/>
							(s)
						</div>
						<small className={styles.helper}>
							Note: Consent Expiry Date will determine the FI Data Range Expiry
							as well.{' '}
						</small>
						<div className={styles['multi-error']}>
							{touched.consentExpiry && errors.consentExpiry ? (
								<Error>{errors.consentExpiry.value}</Error>
							) : null}
							{touched.consentExpiry && errors.consentExpiry ? (
								<Error>{errors.consentExpiry.unit}</Error>
							) : null}
						</div>
					</div>
					<div className={styles.divider}>
						<h3>Data Parameters</h3>
						<Divider />
					</div>
					{/* Data Filter */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="dataFilter" className={styles['input-label']}>
								Data Filter:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} dataFilter-tooltip`}
								alt="tooltip"
								data-pr-tooltip="Enter the data filter for the data to be fetched using this template."
							/>
						</div>
						<Tooltip target=".dataFilter-tooltip" className={styles.tooltip} />
						<div className={styles['options__container']}>
							<span>
								<Dropdown
									name="dataFilter.type"
									value={values.dataFilter.type}
									options={CONSTANTS.dataFilterTypes}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Select type"
									className={styles['periodic-dropdown']}
								/>
							</span>

							<span>
								<Dropdown
									name="dataFilter.operator"
									value={values.dataFilter.operator}
									options={CONSTANTS.dataFilterOperators}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Select operator"
									className={styles['periodic-dropdown']}
								/>
							</span>

							<InputText
								name="dataFilter.value"
								placeholder="Enter value"
								value={values.dataFilter.value}
								onChange={handleChange}
								onBlur={handleBlur}
								className={styles['dataFilter-input']}
							/>
						</div>
						<div className={styles['multi-error']}>
							{touched.dataFilter && errors.dataFilter ? (
								<Error>{errors.dataFilter.type}</Error>
							) : null}
							{touched.dataFilter && errors.dataFilter ? (
								<Error>{errors.dataFilter.operator}</Error>
							) : null}
							{touched.dataFilter && errors.dataFilter ? (
								<Error>{errors.dataFilter.value}</Error>
							) : null}
						</div>
					</div>

                    {/* Financial Data Range */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="dataRange" className={styles['input-label']}>
								Financial Data Range:
							</label>
							<img
							        src={infoIcon}
									className={`${styles.info} dataRange-tooltip`}
									alt="tooltip"
									data-pr-tooltip="Enter the time range for the data to be fetched using this template."
							/>
						</div>
						<Tooltip target=".dataRange-tooltip" className={styles.tooltip} />
						<div className={styles['options__container']}>
							Fetch data from last
							<span>
							<Dropdown
							name="dataRange.unit"
							value={values.dataRange.unit}
							options={CONSTANTS.dataRangeList}
							onChange={handleDataRangeUnitChange}
							onBlur={handleBlur}
							placeholder="Select a time range"
							className={styles['periodic-dropdown']}
							disabled={false}
							/>
							</span>
							month(s) for the next
							<InputText
								keyfilter="int"
								name="dataRange.value"
								value={values.dataRange.value}
								onChange={handleDataRangeValueChange}
								onBlur={handleBlur}
								className={styles['periodic-input']}
							/>
							<Dropdown
							name="dataRangeEndUnit"
							value={values.dataRangeEndUnit}
							options={CONSTANTS.dateUnitList}
							onChange={handleConsentExpiryUnitChange}
							onBlur={handleBlur}
							placeholder="Select unit"
							className={styles['periodic-dropdown']}
							disabled={false}
							/>
						(s)
						</div>
						<div className={styles['multi-error']}>
							{touched.dataRange && errors.dataRange ? (
								<Error>{errors.dataRange.unit}</Error>
							) : null}
							{touched.dataRange && errors.dataRange ? (
								<Error>{errors.dataRange.value}</Error>
							) : null}
							{touched.dataRange && errors.dataRange ? (
								<Error>{errors.dataRange.valueUnit}</Error>
								) : null}
						</div>
					</div>
					{/* Data Life */}
					<div className={styles['input__container']}>
						<div className={styles['label__container']}>
							<label htmlFor="dataLife" className={styles['input-label']}>
								Data Life:
							</label>
							<img
								src={infoIcon}
								className={`${styles.info} dataLife-tooltip`}
								alt="tooltip"
								data-pr-tooltip="This represents the period that the FIU may process the data, once consented to, by the citizen."
							/>
						</div>
						<Tooltip target=".dataLife-tooltip" className={styles.tooltip} />
						<div className={styles['options__container']}>
							Data will be stored for
							<span>
								<InputText
									keyfilter="int"
									name="dataLife.value"
									value={values.dataLife.value}
									onChange={handleDataLifeValueChange}
									onBlur={handleBlur}
									className={styles['periodic-input']}
								/>
							</span>
							<Dropdown
								name="dataLife.unit"
								value={values.dataLife.unit}
								options={CONSTANTS.dateUnitList}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder="Select an data life date unit"
								className={styles['periodic-dropdown']}
								disabled={fieldsPrefilledFromAPI && values.dataLife.unit}
							/>
						</div>
						<small className={styles.helper}>
							Note: This is different from Data Range. To learn more, click{' '}
							<a
								href="https://sahamati.org.in/aa-community-guidelines-v1-1/consent-request-management/"
								target="_blank"
								rel="noreferrer noopener"
							>
								here.
							</a>{' '}
							(CR004)
						</small>
						<div className={styles['multi-error']}>
							{touched.dataLife && errors.dataLife ? (
								<Error>{errors.dataLife.value}</Error>
							) : null}
							{touched.dataLife && errors.dataLife ? (
								<Error>{errors.dataLife.unit}</Error>
							) : null}
						</div>
					</div>

					<div className={styles['button__container']}>
						<Button
							label={isEditable ? 
								'Update Consent Template' :
								'Create Consent Template'
							}
						></Button>
						<Button
						typeof="button"
						label="Reset"
						className={`${styles.button} ${styles['reset-button']}`}
						style={{ marginLeft: '1rem'}}
						onClick={() => resetForm()}
						></Button>
					</div>
				</div>
			</form>
		</div>
	);
};

export default CreateTemplates;